import { FrequencyUnit } from "common/types";
import { FreeShippingStatus } from "common/types/carts";

import type { PersonalizationKind } from "client/products/types";
import {
  ClientPasswordResetRequestErrorKind,
  ClientSignupErrorKind,
  LoginErrorKind,
  SignupVerificationCodeErrorKind,
} from "client/accounts/types";

/**
 * GA4 ecommerce event names sent via gtag. Partners use these to trigger events in their
 * Google Tag Manager setup. Ensure these names are kept consistent with the documentation
 * we've shared with them here:
 * https://docs.google.com/document/d/1sYGNv_llIEIvoFTq2Z9fX12oGn3BT46NgAlDYM0LsI0/
 */
export enum EcommerceEventName {
  Purchase = "purchase",
  AddToCart = "add_to_cart",
  RemoveFromCart = "remove_from_cart",
  ViewItem = "view_item",
  SelectItem = "select_item",
  ViewItemList = "view_item_list",
  BeginCheckout = "begin_checkout",
  AddShippingInfo = "add_shipping_info",
  AddPaymentInfo = "add_payment_info",
}

/**
 * Custom (non-GA4) analytics event names pushed directly to the dataLayer.
 * Each event gets a `vetcove_` prefix when fired (e.g. `vetcove_page_view`) to
 * namespace it away from GA4 reserved names and any other tags in the container.
 */
export enum CustomAnalyticsEventName {
  PageView = "page_view",
}

export enum PostHogEventName {
  AddToCart = "add_to_cart",
  AddToCartFromLink = "add_to_cart_from_link",
  BeginCheckout = "begin_checkout",
  CheckoutCompleted = "checkout_completed",
  FreeShippingProgressView = "free_shipping_progress_view",
  ItemClick = "item_click",
  ItemsView = "items_view",
  ProductClick = "product_click",
  ProductLineClick = "product_line_click",
  ProductLineView = "product_line_view",
  ProductListFilterChange = "product_list_filter_change",
  ProductLinesView = "product_lines_view",
  ProductsView = "products_view",
  RemoveFromCart = "remove_from_cart",
  UpdateCartItemAutoship = "update_cart_item_autoship",
  UpdatePaymentMethod = "update_payment_method",
  UpdateShippingMethod = "update_shipping_method",
  LoginAttempted = "login_attempted",
  LoginFailed = "login_failed",
  LoginSucceeded = "login_succeeded",
  PasswordResetRequested = "password_reset_requested",
  PasswordResetRequestSucceeded = "password_reset_request_succeeded",
  PasswordResetRequestFailed = "password_reset_request_failed",
  SignupCompleted = "signup_completed",
  SignupFailed = "signup_failed",
  SignupCodeRequested = "signup_code_requested",
  SignupCodeRequestSucceeded = "signup_code_request_succeeded",
  SignupCodeRequestFailed = "signup_code_request_failed",
}

export type AuthUsernameType = "email" | "phone";

/**
 * Flat item shape sent to GTM. Matches the GA4 ecommerce item schema.
 * Standard GA4 ecommerce item fields.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item_list_item
 */
type GA4EcommerceItemFields = {
  item_id?: string;
  item_name?: string;
  item_brand?: string;
  item_category?: string;
  /** Comma-separated list of non-primary category names on the product line. */
  item_category2?: string;
  price?: number;
  discount?: number;
  quantity?: number;
  index?: number;
};

/** Custom Vetcove fields added to ecommerce items for V2 events. */
type VetcoveEcommerceItemFields = {
  vetcove_item_id?: string;
  vetcove_item_name?: string;
  product_id?: string;
  product_name?: string;
  prescription_required?: boolean;
  manufacturer_name?: string;
  days_supply?: number;
  frequency?: string;
};

export type EcommerceItem = GA4EcommerceItemFields & VetcoveEcommerceItemFields;

/**
 * A generic type for an Ecommerce event we send to google tag manager.
 * Generally these follow the format of GA4 ecommerce events.
 *
 * Includes a *name* that corresponds to one of the events found at
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 * and the related *data* we will send alongwith it
 */
export type EcommerceEventType<T extends EcommerceEventName, E extends object> = {
  name: T;
  data: E;
};

/**
 * Generic type for a custom (non-GA4) analytics event pushed to the dataLayer.
 * The *name* is a value from CustomAnalyticsEventName; sendDataLayerEvent
 * prefixes it with `vetcove_` before dispatch.
 */
export type CustomAnalyticsEventType<T extends CustomAnalyticsEventName, E extends object> = {
  name: T;
  data: E;
};

export type PostHogEventType<T extends PostHogEventName, E extends object> = {
  name: T;
  data: E;
};

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase */
export type EcommercePurchaseEvent = EcommerceEventType<
  EcommerceEventName.Purchase,
  {
    currency: "USD";
    /** Some unique identifier to avoid duplicate purchase events */
    transaction_id: string;
    /** Dollar value of the transaction */
    value: number;
    shipping: number;
    tax: number;
    items: EcommerceItem[];
    coupon?: string;
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart */
export type EcommerceAddToCartEvent = EcommerceEventType<
  EcommerceEventName.AddToCart,
  {
    currency: "USD";
    value: number;
    /** The set of items added to the cart */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#remove_from_cart */
export type EcommerceRemoveFromCartEvent = EcommerceEventType<
  EcommerceEventName.RemoveFromCart,
  {
    currency: "USD";
    value: number;
    /** The set of items removed from the cart */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item */
export type EcommerceViewItemEvent = EcommerceEventType<
  EcommerceEventName.ViewItem,
  {
    currency: "USD";
    value: number;
    /** The set of items viewed */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#select_item */
export type EcommerceSelectItemEvent = EcommerceEventType<
  EcommerceEventName.SelectItem,
  {
    /* The name of the list the item is viewed in */
    item_list_name: string;
    /** The set of items selected from the list */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item_list */
export type EcommerceViewItemListEvent = EcommerceEventType<
  EcommerceEventName.ViewItemList,
  {
    /* The name of the list the item is viewed in */
    item_list_name: string;
    /** The set of items in the list */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#begin_checkout */
export type EcommerceBeginCheckoutEvent = EcommerceEventType<
  EcommerceEventName.BeginCheckout,
  {
    currency: "USD";
    value: number;
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_shipping_info */
export type EcommerceAddShippingInfoEvent = EcommerceEventType<
  EcommerceEventName.AddShippingInfo,
  {
    currency: "USD";
    value: number;
    coupon?: string;
    shipping_tier: string;
    items: EcommerceItem[];
  }
>;

export type EcommercePaymentType = "klarna" | "apple_pay" | "google_pay" | "credit_card";

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_payment_info */
export type EcommerceAddPaymentInfoEvent = EcommerceEventType<
  EcommerceEventName.AddPaymentInfo,
  {
    currency: "USD";
    value: number;
    coupon?: string;
    payment_type: EcommercePaymentType;
    items: EcommerceItem[];
  }
>;

/**
 * page_view event for SPA virtual pageview tracking.
 * Fires on every page load and client-side route change.
 *
 * Standard GA4 parameters: page_location, page_referrer, page_title
 * Custom parameters: page_name, page_type
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#page_view
 */
export type PageViewData = {
  /** Full URL (e.g., https://example.com/pet/products/123) — standard GA4 parameter */
  page_location: string;
  /** Previous URL — document.referrer on initial load, previous internal URL for SPA navigations — standard GA4 parameter */
  page_referrer: string;
  /** document.title, falling back to pathname — standard GA4 parameter */
  page_title: string;
  /** Unique page identifier (e.g., "product-line", "checkout-items") — custom */
  page_name: string;
  /** Feature area / module (e.g., "products", "checkout") — custom */
  page_type: string;
};

export type PageViewEvent = CustomAnalyticsEventType<
  CustomAnalyticsEventName.PageView,
  PageViewData
>;

export type PostHogCheckoutCompletedEvent = PostHogEventType<
  PostHogEventName.CheckoutCompleted,
  {
    id: number;
    cart_id: number;
    client_id: number;
    clinic_id: number;
    payment_method_id: number | null;
    shipping_address_id: number | null;
    total: number;
    discounted_subtotal: number;
    tax: number;
    shipping: number;
    promo_code_ids: number[];
    order_items: {
      id: number;
      item_id: number;
      patient_id: number | null;
      prescription_fill_id: number | null;
      quantity: number;
      unit_price: number;
      amortized_discount: number;
      status: string | null;
    }[];
  }
>;

/** Where an add-to-cart happened when it wasn't on a product list (see ProductListSource). */
export enum AddToCartPageSource {
  ProductDetailPage = "Product Detail Page",
  Checkout = "Checkout",
  CartItem = "Cart Item",
  Subscription = "Subscription",
  OrderItem = "Order Item",
  Prescription = "Prescription",
}

/** Shares strings with product_click / products_view so one `source` filter follows list → click → add. */
export type AddToCartSource = ProductListSource | AddToCartPageSource;

export type PostHogAddToCartEvent = PostHogEventType<
  PostHogEventName.AddToCart,
  {
    source: AddToCartSource;
    cart_id: number | null;
    cart_item_id: number;
    item_id: number;
    product_id: number;
    product_line_id: number;
    patient_id: number | null;
    flavor_selection_id: number | null;
    quantity: number;
    unit_price: number;
    discount: number | null;
    /** The collection page the add happened on. */
    collection_slug?: string;
    category_id?: number;
  }
>;

export type PostHogAddToCartFromLinkEvent = PostHogEventType<
  PostHogEventName.AddToCartFromLink,
  {
    cart_id: number | null;
    cart_item_id: number;
    item_id: number;
    patient_id: number | null;
    quantity: number;
  }
>;

export type PostHogRemoveFromCartEvent = PostHogEventType<
  PostHogEventName.RemoveFromCart,
  {
    cart_id: number;
    cart_item_id: number;
    item_id: number;
    patient_id: number | null;
    flavor_selection_id: number | null;
    quantity: number;
    unit_price: number;
    discount: number;
  }
>;

export type PostHogUpdateCartItemAutoshipEvent = PostHogEventType<
  PostHogEventName.UpdateCartItemAutoship,
  {
    cart_id: number;
    cart_item_id: number;
    item_id: number;
    is_enabled: boolean;
    /** The chosen schedule, null when Autoship is being turned off. */
    frequency: number | null;
    interval_unit: FrequencyUnit | null;
  }
>;

export type PostHogProductLineViewEvent = PostHogEventType<
  PostHogEventName.ProductLineView,
  {
    id: number;
    name: string;
    category_ids: number[];
    prescription_required: boolean;
    is_compound: boolean;
    product_id: number;
    item_id: number;
    standard_price: number;
    autoship_price: number;
  }
>;

export enum ProductLineListSource {
  CategoryPage = "Category Page",
  HomePageFeatured = "Home Page - Featured",
  HomePageForYou = "Home Page - For You",
  HomePageCategory = "Home Page - Category",
  BrowseCatalogFeatured = "Browse Catalog - Featured",
  BrowseCatalogForYou = "Browse Catalog - For You",
  BrowseCatalogCategory = "Browse Catalog - Category",
  MyZoetisRewards = "My Zoetis Rewards",
  CategoryPageProductLines = "Category Page - Product Lines",
}

export type PostHogProductLineClickEvent = PostHogEventType<
  PostHogEventName.ProductLineClick,
  {
    source: ProductLineListSource;
    id: number;
    category_id: number | null;
    personalization_kind: PersonalizationKind | null;
    /** Zero-based slot in the row it was clicked from. */
    position?: number;
  }
>;

/** Where on a product list the shopper changed a filter. */
export enum ProductListFilterSurface {
  /** The tier-2 category pills on a tier-1 category page. */
  Subcategories = "subcategories",
  /** The filter panel beside the results. */
  Rail = "rail",
  /** The filter panel in a modal on narrow viewports, sent when the shopper applies it. */
  Drawer = "drawer",
  /** The removable chips for the selected values, and their Clear all. */
  AppliedFilters = "applied_filters",
}

/** A shopper selecting or clearing one filter value on a product list. */
export type PostHogProductListFilterChangeEvent = PostHogEventType<
  PostHogEventName.ProductListFilterChange,
  {
    source: ProductListSource;
    /** The filter's key, e.g. "category". */
    filter: string;
    value: string | number | boolean;
    selected: boolean;
    /** The filter's selected values after the change. */
    selected_values: (string | number | boolean)[];
    surface: ProductListFilterSurface;
    /** Zero-based slot in the surface, when it is a row. */
    position?: number;
    category_id?: number;
  }
>;

export enum ProductListSource {
  SearchResults = "Search Results",
  DealsPage = "Deals Page",
  BrandsPage = "Brands Page",
  DealsCollectionPage = "Deals Collection Page",
  CategoryPage = "Category Page",
  RebateDrawer = "Rebate Drawer",
  IncentiveDrawer = "Incentive Drawer",
  RebatePage = "Rebate Page",
  IncentivePage = "Incentive Page",
}

export type PostHogProductClickEvent = PostHogEventType<
  PostHogEventName.ProductClick,
  {
    source: ProductListSource;
    id: number;
    product_line_id: number;
    default_item_id?: number;
    /** Which collection the card was clicked on; `source` alone cannot say once more than
     * one collection page exists. */
    collection_slug?: string;
    category_id?: number;
  }
>;

export enum ItemClickSource {
  Prescription = "Prescription",
  Subscription = "Subscription",
  CartItem = "Cart Item",
  OrderItem = "Order Item",
  OrderSupport = "Order Support",
  OrderItemModification = "Order Item Modification",
  CrossSell = "Cross Sell",
  Rebate = "Rebate",
  Incentive = "Incentive",
}

export type PostHogItemClickEvent = PostHogEventType<
  PostHogEventName.ItemClick,
  {
    source: ItemClickSource;
    id: number;
    product_id: number;
    product_line_id: number;
    unit_price: number | null;
  }
>;

export type PostHogItemsViewEvent = PostHogEventType<
  PostHogEventName.ItemsView,
  {
    source: ItemClickSource;
    item_ids: number[];
  }
>;

export type PostHogProductsViewEvent = PostHogEventType<
  PostHogEventName.ProductsView,
  {
    source: ProductListSource;
    product_ids: number[];
    search_term?: string;
    collection_slug?: string;
    category_id?: number;
    promotion_id?: number;
    /** Total matches, not the page size. */
    num_results?: number;
    /** Search request time in ms, measured in the browser. Absent for results served from cache. */
    latency_ms?: number;
  }
>;

export type PostHogProductLinesViewEvent = PostHogEventType<
  PostHogEventName.ProductLinesView,
  {
    source: ProductLineListSource;
    product_line_ids: number[];
    personalization_kinds: (PersonalizationKind | null)[];
    category_id: number | null;
  }
>;

/** Where the free shipping progress banner rendered */
export enum FreeShippingProgressSurface {
  CartDrawer = "Cart Drawer",
  CheckoutSummary = "Checkout Summary",
  ItemOptionsSummary = "Item Options Summary",
  ReviewOrderSummary = "Review Order Summary",
}

export type PostHogFreeShippingProgressViewEvent = PostHogEventType<
  PostHogEventName.FreeShippingProgressView,
  {
    cart_id: number;
    surface: FreeShippingProgressSurface;
    status: FreeShippingStatus;
    amount_remaining: number;
    paid_shipment_count: number;
  }
>;

export type PostHogBeginCheckoutEvent = PostHogEventType<
  PostHogEventName.BeginCheckout,
  {
    cart_id: number;
    client_id: number;
    clinic_id: number;
    subtotal: number;
    discount: number;
    shipping: number;
    total: number;
    promo_code_ids: number[];
    // Both free shipping fields: sent whether or not the banner flag is on, so both experiment
    // arms compare; null when the cart has no free shipping progress
    free_shipping_status: FreeShippingStatus | null;
    free_shipping_amount_remaining: number | null;
    cart_items: {
      id: number;
      item_id: number;
      patient_id: number | null;
      flavor_selection_id: number | null;
      quantity: number;
      unit_price: number;
      discount: number;
    }[];
  }
>;

export type PostHogUpdateShippingMethodEvent = PostHogEventType<
  PostHogEventName.UpdateShippingMethod,
  {
    cart_id: number;
    client_id: number;
    clinic_id: number;
    shipping_address_id: number | null;
    vendor_slug: string | null;
    shipping_method_id: number;
    shipping_method_title: string;
    shipping_cost: number;
    cart_item_ids: number[];
  }
>;

export type PostHogUpdatePaymentMethodEvent = PostHogEventType<
  PostHogEventName.UpdatePaymentMethod,
  {
    cart_id: number;
    client_id: number;
    clinic_id: number;
    payment_method_id: number | null;
    payment_type: string;
    wallet_type: string | null;
    card_brand: string | null;
  }
>;

export type PostHogLoginAttemptedEvent = PostHogEventType<
  PostHogEventName.LoginAttempted,
  {
    username: string;
  }
>;

export type PostHogLoginFailedEvent = PostHogEventType<
  PostHogEventName.LoginFailed,
  {
    username: string;
    error_category: LoginErrorKind | null;
    error_message: string | null;
  }
>;

export type PostHogLoginSucceededEvent = PostHogEventType<
  PostHogEventName.LoginSucceeded,
  {
    username: string;
  }
>;

export type PostHogPasswordResetRequestedEvent = PostHogEventType<
  PostHogEventName.PasswordResetRequested,
  {
    username: string;
  }
>;

export type PostHogPasswordResetRequestSucceededEvent = PostHogEventType<
  PostHogEventName.PasswordResetRequestSucceeded,
  {
    username: string;
  }
>;

export type PostHogPasswordResetRequestFailedEvent = PostHogEventType<
  PostHogEventName.PasswordResetRequestFailed,
  {
    username: string;
    error_category: ClientPasswordResetRequestErrorKind | null;
    error_message: string | null;
  }
>;

export type PostHogSignupCompletedEvent = PostHogEventType<
  PostHogEventName.SignupCompleted,
  {
    email: string;
    phone: string;
    first_name: string;
    last_name: string;
    username_type: AuthUsernameType;
  }
>;

export type PostHogSignupFailedEvent = PostHogEventType<
  PostHogEventName.SignupFailed,
  {
    email: string;
    phone: string;
    first_name: string;
    last_name: string;
    username_type: AuthUsernameType;
    error_category: ClientSignupErrorKind | null;
    error_message: string | null;
  }
>;

export type PostHogSignupCodeRequestedEvent = PostHogEventType<
  PostHogEventName.SignupCodeRequested,
  {
    username_type: AuthUsernameType;
    is_resend: boolean;
  }
>;

export type PostHogSignupCodeRequestSucceededEvent = PostHogEventType<
  PostHogEventName.SignupCodeRequestSucceeded,
  {
    username_type: AuthUsernameType;
    is_resend: boolean;
  }
>;

export type PostHogSignupCodeRequestFailedEvent = PostHogEventType<
  PostHogEventName.SignupCodeRequestFailed,
  {
    username_type: AuthUsernameType;
    is_resend: boolean;
    error_category: SignupVerificationCodeErrorKind | null;
    error_message: string | null;
  }
>;

export type CustomAnalyticsEvent = PageViewEvent;

export type PostHogEvent =
  | PostHogAddToCartEvent
  | PostHogBeginCheckoutEvent
  | PostHogCheckoutCompletedEvent
  | PostHogFreeShippingProgressViewEvent
  | PostHogItemClickEvent
  | PostHogItemsViewEvent
  | PostHogAddToCartFromLinkEvent
  | PostHogProductClickEvent
  | PostHogProductLineViewEvent
  | PostHogProductLinesViewEvent
  | PostHogProductListFilterChangeEvent
  | PostHogProductsViewEvent
  | PostHogRemoveFromCartEvent
  | PostHogUpdateCartItemAutoshipEvent
  | PostHogUpdatePaymentMethodEvent
  | PostHogUpdateShippingMethodEvent
  | PostHogLoginAttemptedEvent
  | PostHogLoginFailedEvent
  | PostHogLoginSucceededEvent
  | PostHogPasswordResetRequestedEvent
  | PostHogPasswordResetRequestSucceededEvent
  | PostHogPasswordResetRequestFailedEvent
  | PostHogSignupCompletedEvent
  | PostHogSignupFailedEvent
  | PostHogSignupCodeRequestedEvent
  | PostHogSignupCodeRequestSucceededEvent
  | PostHogSignupCodeRequestFailedEvent;

export type EcommerceEvent =
  | EcommercePurchaseEvent
  | EcommerceAddToCartEvent
  | EcommerceRemoveFromCartEvent
  | EcommerceViewItemEvent
  | EcommerceSelectItemEvent
  | EcommerceViewItemListEvent
  | EcommerceBeginCheckoutEvent
  | EcommerceAddShippingInfoEvent
  | EcommerceAddPaymentInfoEvent;

/**
 * Product line level data for analytics events.
 *
 * Uses structural typing so domain objects (IItemProductLine, ProductLineResult, etc.)
 * can be passed directly without remapping.
 *
 * `manufacturer` is only on PL search (per NVA; other views intentionally skip it).
 */
export type AnalyticsProductLineData = {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  primary_category_name: string;
  categories: { name: string }[];
  manufacturer?: { id: number; name: string };
};

/** Product level data for analytics events (includes nested product line). */
export type AnalyticsProductData = {
  id: number;
  name: string;
  product_line: AnalyticsProductLineData;
};

export const isAnalyticsItemData = (data: AnalyticsItemEventData): data is AnalyticsItemData =>
  "product" in data;

export const isAnalyticsProductData = (
  data: AnalyticsItemEventData,
): data is AnalyticsProductData => "product_line" in data;

/** Item level data for analytics events (includes nested product and product line). */
export type AnalyticsItemData = {
  id: number;
  name: string;
  product: AnalyticsProductData;
  days_supply: number;
  duration_value: number;
  duration_unit: string;
};

/**
 * Union of all item event data levels. Used by sendViewItemList, sendSelectItem and the
 * other item events, and through `AnalyticsCartItem.item` by the cart events.
 *
 * Pass whichever level your domain object provides — the deeper the data, the
 * richer the GTM event, but all levels produce valid EcommerceItems.
 *
 * Discriminated at runtime via 'product' in data / 'product_line' in data.
 */
export type AnalyticsItemEventData =
  | AnalyticsProductLineData
  | AnalyticsProductData
  | AnalyticsItemData;

/**
 * Cart item for analytics events. Used by sendAddToCart, sendRemoveFromCart.
 * Consumers provide pre-computed price and discount.
 *
 * ICartItem consumers use getCartItemPricing() to resolve pricing.
 * Non-cart consumers (orders, subscriptions, prescriptions) pass their
 * own computed pricing directly.
 */
export type AnalyticsCartItem = {
  item: AnalyticsItemEventData;
  quantity: number;
  price: number;
  discount: number;
};
